import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Download, Lock, FileText, CheckCircle, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BDCheckpoints() {
  const [params] = useSearchParams();
  const sourceParam = params.get('src') || params.get('source') || null;

  const utm = useMemo(() => {
    const u = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((k) => {
      const v = params.get(k);
      if (v) u[k] = v;
    });
    return Object.keys(u).length ? u : null;
  }, [params]);

  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    intent: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/bd/checkpoints/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim() || null,
          role: form.role.trim() || null,
          intent: form.intent.trim() || null,
          source: sourceParam,
          utm,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = typeof data?.detail === 'string'
          ? data.detail
          : Array.isArray(data?.detail) ? data.detail.map((d) => d.msg).join(', ')
          : 'Something went wrong. Please try again.';
        throw new Error(detail);
      }
      const fullUrl = `${API_URL}${data.download_url}`;
      setDownloadUrl(fullUrl);
      // Auto-open in a new tab so the file downloads immediately.
      window.open(fullUrl, '_blank', 'noopener');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />

      <section className="pt-32 pb-16 px-6">
        <div className="container mx-auto max-w-5xl">
          <div className="grid md:grid-cols-5 gap-10">
            {/* LEFT — context column */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="md:col-span-3 space-y-6"
              data-testid="bd-checkpoints-context"
            >
              <div className="inline-flex items-center gap-2 text-xs tracking-[0.25em] text-gray-400 uppercase">
                <Lock className="w-3.5 h-3.5" /> BD Signal Tracker · Confidential
              </div>
              <h1 className="text-4xl md:text-5xl font-bold leading-[1.1]">
                Agentic Payments
                <br />
                <span className="bg-gradient-to-r from-orange-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
                  Checkpoints Tracking
                </span>
              </h1>
              <p className="text-gray-300 text-lg max-w-prose">
                Twelve observable checkpoints across Ching Tseng&apos;s three-path thesis —
                <em> Replace · Integrate · Create</em> — calibrated to Kojiru&apos;s deterministic
                underwriting wedge.
              </p>

              <ul className="space-y-3 text-sm text-gray-300">
                <li className="flex gap-3">
                  <span className="mt-0.5 inline-block w-1.5 h-1.5 rounded-full bg-orange-400" />
                  <span><strong className="text-white">Replace path</strong> — crypto-native rails displacing card / ACH.</span>
                </li>
                <li className="flex gap-3">
                  <span className="mt-0.5 inline-block w-1.5 h-1.5 rounded-full bg-blue-400" />
                  <span><strong className="text-white">Integrate path</strong> — Mastercard, Visa, Stripe bolting on agent + stablecoin settlement.</span>
                </li>
                <li className="flex gap-3">
                  <span className="mt-0.5 inline-block w-1.5 h-1.5 rounded-full bg-purple-400" />
                  <span><strong className="text-white">Create path</strong> — pay-per-call endpoints, agent-to-agent settlement, ACS-priced insurance.</span>
                </li>
                <li className="flex gap-3">
                  <FileText className="w-4 h-4 mt-0.5 text-gray-400 shrink-0" />
                  <span>One page. Lead indicators, counterparties to watch, and the Kojiru angle for every checkpoint.</span>
                </li>
              </ul>

              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 text-sm text-gray-400">
                <div className="text-white font-medium mb-1">Why we gate this</div>
                We share this with institutional partners who are actively building or
                underwriting in the agentic-payments space. A short intro tells us where
                the signal is traveling.
              </div>

              <div className="text-xs text-gray-500 pt-2">
                Agent Credit Score (ACS)™ · Kojiru AI · GENIUS Act Aligned · Payment Stablecoin Infrastructure.
              </div>
            </motion.div>

            {/* RIGHT — gate form */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="md:col-span-2"
            >
              <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur p-7">
                {downloadUrl ? (
                  <div className="text-center py-6" data-testid="bd-checkpoints-success">
                    <div className="w-14 h-14 rounded-full bg-green-500/15 flex items-center justify-center mx-auto mb-5">
                      <CheckCircle className="w-7 h-7 text-green-400" />
                    </div>
                    <h3 className="text-2xl font-bold mb-2">You&apos;re in.</h3>
                    <p className="text-gray-400 text-sm mb-6">
                      Your download should have opened in a new tab. If it didn&apos;t, use
                      the button below — the link is valid for 24 hours.
                    </p>
                    <a
                      href={downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="bd-checkpoints-download-link"
                    >
                      <Button className="w-full h-11 bg-white text-black hover:bg-gray-200">
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                        <ExternalLink className="w-3.5 h-3.5 ml-2 opacity-60" />
                      </Button>
                    </a>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="bd-checkpoints-form">
                    <h2 className="text-xl font-semibold mb-1">Get the PDF</h2>
                    <p className="text-xs text-gray-400 mb-4">
                      One page · institutional-grade · no list, no follow-up sequence.
                    </p>

                    <Field label="Name" required>
                      <input
                        type="text"
                        required
                        value={form.name}
                        onChange={update('name')}
                        className={inputCls}
                        placeholder="Your name"
                        data-testid="bd-input-name"
                      />
                    </Field>

                    <Field label="Work email" required>
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={update('email')}
                        className={inputCls}
                        placeholder="you@company.com"
                        data-testid="bd-input-email"
                      />
                    </Field>

                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Company">
                        <input
                          type="text"
                          value={form.company}
                          onChange={update('company')}
                          className={inputCls}
                          placeholder="Circle, Mastercard, …"
                          data-testid="bd-input-company"
                        />
                      </Field>
                      <Field label="Role">
                        <input
                          type="text"
                          value={form.role}
                          onChange={update('role')}
                          className={inputCls}
                          placeholder="BD, Eng, Research"
                          data-testid="bd-input-role"
                        />
                      </Field>
                    </div>

                    <Field label="What are you working on? (optional)">
                      <textarea
                        rows={3}
                        value={form.intent}
                        onChange={update('intent')}
                        className={`${inputCls} resize-none`}
                        placeholder="Agentic payments rail, underwriting layer, integration spec…"
                        data-testid="bd-input-intent"
                      />
                    </Field>

                    {error && (
                      <div
                        className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2"
                        data-testid="bd-checkpoints-error"
                      >
                        {error}
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full h-11 bg-gradient-to-r from-orange-500 via-blue-500 to-purple-500 hover:opacity-90 disabled:opacity-50"
                      data-testid="bd-checkpoints-submit"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {submitting ? 'Preparing your link…' : 'Get the PDF'}
                    </Button>

                    <p className="text-[10px] leading-snug text-gray-500 pt-1">
                      We use your details only to know who is reading. No marketing
                      automation. Read our{' '}
                      <a href="/privacy" className="underline hover:text-gray-300">privacy policy</a>.
                    </p>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const inputCls =
  'w-full px-3.5 py-2.5 bg-white/[0.04] border border-white/10 rounded-lg text-sm focus:outline-none focus:border-blue-500/70 placeholder:text-gray-600';

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1.5 text-gray-300">
        {label}
        {required && <span className="text-orange-400 ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}
